// Circuits : ce qui arrive (mails, pièces, opérations), par quel canal, quelle règle s'applique et
// où cela aboutit, en flux chiffrés pour le diagramme de la page « Circuits » ; et l'état des
// automatismes (jobs du worker) pour l'audit quotidien.

import type { ClassementMailCode, ModeTransmission, SourcePiece, StatutPiece } from './domaine'

/** Qui fait l'étape : la couleur du nœud dans le diagramme. */
export const GENRES_NOEUD = [
  'entree',
  'regle',
  'claude',
  'humain',
  'ecarte',
  'abouti',
  'echec',
] as const
export type GenreNoeud = (typeof GENRES_NOEUD)[number]

export const LIBELLES_GENRE_NOEUD: Record<GenreNoeud, string> = {
  entree: 'Entrée',
  regle: 'Règle, sans modèle',
  claude: 'Claude',
  humain: 'Humain',
  ecarte: 'Écarté',
  abouti: 'Abouti',
  echec: 'Échec',
}

export type UniteFlux = 'mail' | 'piece' | 'operation'

export type NoeudFlux = {
  id: string
  libelle: string
  colonne: number
  genre: GenreNoeud
  /** Source d'un canal (première colonne) : « Mails », « Portails »… */
  groupe?: string
  description: string
  /** Règles en place derrière le nœud (consignes « ignorer »…), en clair. */
  details?: string[]
  /** Automatismes (jobs) qui font l'étape. */
  jobs?: string[]
  /** Écran où agir, pour ce qui attend un humain. */
  lien?: '/taches' | '/pieces' | '/releves' | '/reglages'
}

export type LienFlux = { de: string; vers: string; valeur: number; unite: UniteFlux }

export type Flux = { colonnes: string[]; noeuds: NoeudFlux[]; liens: LienFlux[] }

/** Mails triés d'une boîte personnelle (adresse), ou ayant apporté des pièces sur admin@ (null). */
export type FaitMail = {
  boite: string | null
  classement: ClassementMailCode | null
  /** Sujet de la consigne de tri qui l'a guidé. */
  consigne: string | null
  n: number
}

/** Pièces arrivées, regroupées par ce qui leur est arrivé. */
export type FaitPiece = {
  source: SourcePiece
  /** Libellé de la connexion d'un portail. */
  connexion: string | null
  statut: StatutPiece
  /** Dernier rattachement noté au journal : par un humain, et la règle qui l'a couvert. */
  rattachement: {
    humain: boolean
    regle: string | null
    consigneFournisseur: string | null
  } | null
  /** Dernier classement noté : placée par Claude, ou classée par un humain. */
  classement: 'placer' | 'classer' | null
  dossier: boolean
  /** L'entité a un plan de classement (racine dans le Drive). */
  racine: boolean
  rangement: 'a_faire' | 'en_attente' | 'fait' | null
  echecDrive: boolean
  transmission: string | null
  echecCabinet: boolean
  /**
   * Mode de transmission de l'entité (`entites.transmission`) ; absent ou null : lu comme avant
   * (envoi par mail). En dépôt direct, le cabinet a ses propres nœuds (« Déposée dans Tiime »).
   */
  modeTransmission?: ModeTransmission | null
  n: number
}

/** Opérations bancaires relevées, par compte et par issue du rapprochement. */
export type FaitOperation = {
  compte: string
  etat: 'auto' | 'manuel' | 'a_trancher' | 'sans'
  n: number
}

export const COLONNES_FLUX = [
  'Canal',
  'Tri du mail',
  'Nature',
  'Rattachement',
  'Classement',
  'Drive',
  'Cabinet',
]

const MAILS = 'Mails'

const DEPOTS = 'Dépôts'

/** Ordre des sources dans la première colonne. */
const ORDRE_GROUPES = [MAILS, DEPOTS, 'Portails', 'Plateforme', 'Banque']

/** Dans une source : boîtes par adresse puis admin@, dépôt puis assistant, le reste par libellé. */
function ordreCanal(n: NoeudFlux): string {
  const dernier = n.id === 'canal:admin' || n.id === 'canal:assistant'
  return `${dernier ? 1 : 0} ${n.libelle.toLowerCase()}`
}

function canalDePiece(p: FaitPiece): Omit<NoeudFlux, 'colonne'> | null {
  switch (p.source) {
    case 'gmail':
      return null
    case 'photo':
    case 'depot':
      return {
        id: 'canal:depot',
        libelle: 'Dépôt direct (appli et tél.)',
        genre: 'entree',
        groupe: DEPOTS,
        description:
          'Photo prise avec l’appli (pages assemblées en un PDF chaque minute) ou fichier déposé sur la page Pièces ou dans une tâche, avec une indication facultative.',
        jobs: ['photos.assembler'],
      }
    case 'mcp':
      return {
        id: 'canal:assistant',
        libelle: 'Assistant (Claude)',
        genre: 'entree',
        groupe: DEPOTS,
        description: 'Fichier de la conversation déposé par l’assistant dans sa tâche.',
      }
    case 'portail':
      return {
        id: `canal:portail:${p.connexion ?? ''}`,
        libelle: p.connexion ?? 'Portail',
        genre: 'entree',
        groupe: 'Portails',
        description:
          'Espace client relevé par le collecteur, lancé à la demande (page Collecteurs ou tâche qui l’attend) ; un humain seulement si le portail demande un code.',
        jobs: ['portails.surveiller'],
      }
    case 'plateforme':
      return {
        id: 'canal:plateforme',
        libelle: 'Plateforme agréée',
        genre: 'entree',
        groupe: 'Plateforme',
        description: 'Factures reçues par la plateforme du cabinet, récupérées chaque nuit.',
        jobs: ['pa.recuperer'],
      }
    case 'remplissage':
      return null
  }
}

const PIECES_JOINTES: Omit<NoeudFlux, 'colonne'> = {
  id: 'nature:pieces',
  libelle: 'Pièces jointes',
  genre: 'regle',
  description:
    'Le mail et ses pièces jointes (PDF, XML) forment un seul dossier : chaque pièce part dans le circuit, la décision éventuelle se prend dans « Trancher le mail ».',
}

function rattachementDe(p: FaitPiece): Omit<NoeudFlux, 'colonne'> {
  const lire = ['piece.extraire']
  const rattacher = ['piece.rattacher']
  if (p.statut === 'recue' || p.statut === 'extraite')
    return {
      id: 'ratt:lecture',
      libelle: 'En lecture',
      genre: 'claude',
      description:
        'Factur-X d’abord, sinon Claude lit la pièce : type, émetteur, montants, date limite, texte intégral.',
      jobs: lire,
    }
  if (p.statut === 'a_verifier')
    return {
      id: 'ratt:a_verifier',
      libelle: 'À vérifier',
      genre: 'humain',
      description:
        'Aucune règle ne couvre la pièce, ou la lecture doute : un humain choisit l’entité (« Vérifier la pièce », ou dans « Trancher le mail »).',
      jobs: rattacher,
      lien: '/pieces',
    }
  if (p.statut === 'doublon' || p.statut === 'ignoree')
    return {
      id: 'ratt:ecartee',
      libelle: 'Doublon ou rejetée',
      genre: 'ecarte',
      description:
        'Même fournisseur et même numéro qu’une pièce déjà reçue, ou rejetée à la main : gardée, ni rangée ni envoyée.',
    }
  const r = p.rattachement
  if (r?.humain)
    return {
      id: 'ratt:humain',
      libelle: 'Décision humaine',
      genre: 'humain',
      description:
        'Entité choisie à la main (appli ou assistant) ; « Toujours pour ce fournisseur » en fait une règle pour la suite.',
    }
  if (r?.consigneFournisseur)
    return {
      id: `ratt:cf:${r.consigneFournisseur}`,
      libelle: r.consigneFournisseur,
      genre: 'regle',
      description:
        'Consigne de fournisseur : il dessert plusieurs endroits, la référence lue sur la pièce (contrat, PDL, adresse…) désigne l’entité.',
      jobs: [...rattacher, 'consignes_fournisseur.reexaminer'],
    }
  switch (r?.regle) {
    case 'fournisseur':
      return {
        id: 'ratt:fournisseur',
        libelle: 'Fournisseur connu',
        genre: 'regle',
        description: 'Fournisseur déjà rencontré : la pièce va à son entité par défaut.',
        jobs: rattacher,
      }
    case 'consigne':
      return {
        id: 'ratt:consigne',
        libelle: 'Consigne de tri du mail',
        genre: 'regle',
        description:
          'La consigne de tri qui a classé le mail désigne aussi l’entité de ses pièces.',
        jobs: rattacher,
      }
    case 'echeance':
      return {
        id: 'ratt:echeance',
        libelle: 'Échéance attendue',
        genre: 'regle',
        description:
          'Avis qui fixe le montant d’une échéance, accusé qui la prouve, ou paiement attendu.',
        jobs: rattacher,
      }
    case 'reponse':
      return {
        id: 'ratt:reponse',
        libelle: 'Réponse attendue',
        genre: 'regle',
        description: 'Réponse du tiers qu’une tâche attendait : rattachée à cette tâche.',
        jobs: rattacher,
      }
    default:
      return {
        id: 'ratt:lue',
        libelle: 'Entité indiquée ou lue',
        genre: 'regle',
        description:
          'Indication du dépôt, entité de la connexion du portail, entité nommée sur la pièce (SIREN, nom) ou entité par défaut du fournisseur.',
        jobs: rattacher,
      }
  }
}

function classementDe(p: FaitPiece): Omit<NoeudFlux, 'colonne'> {
  if (!p.racine)
    return {
      id: 'class:chemins',
      libelle: 'Anciens chemins',
      genre: 'regle',
      description: 'Entité sans plan de classement : chemin fixe par rubrique.',
    }
  if (p.dossier && p.classement === 'classer')
    return {
      id: 'class:main',
      libelle: 'Classée à la main',
      genre: 'humain',
      description: 'Dossier choisi par un humain (« Classer la pièce »).',
    }
  if (p.dossier)
    return {
      id: 'class:claude',
      libelle: 'Placée par Claude',
      genre: 'claude',
      description: 'Claude lit le plan de classement de l’entité et choisit le dossier.',
      jobs: ['piece.placer'],
    }
  return {
    id: 'class:a_classer',
    libelle: 'À classer',
    genre: 'humain',
    description:
      'Claude hésite ou propose un nouveau dossier : un humain choisit (« Classer la pièce »).',
    jobs: ['piece.placer'],
    lien: '/pieces',
  }
}

function driveDe(p: FaitPiece): Omit<NoeudFlux, 'colonne'> {
  const jobs = ['piece.ranger']
  if (p.rangement === 'fait')
    return {
      id: 'drive:rangee',
      libelle: 'Rangée dans le Drive',
      genre: 'abouti',
      description: 'Déposée et renommée dans son dossier.',
      jobs,
    }
  if (p.echecDrive)
    return {
      id: 'drive:echec',
      libelle: 'Drive en échec',
      genre: 'echec',
      description: 'Trois essais sans succès : alerte pour les administrateurs.',
      jobs,
      lien: '/pieces',
    }
  if (p.rangement === 'en_attente')
    return {
      id: 'drive:attente',
      libelle: 'Attend un réglage',
      genre: 'humain',
      description: 'Chemin du Drive manquant ou introuvable : à régler sur la fiche de l’entité.',
      jobs,
      lien: '/pieces',
    }
  return {
    id: 'drive:en_cours',
    libelle: 'Pas encore rangée',
    genre: 'regle',
    description: 'Attend son dossier, ou le prochain passage du rangement.',
    jobs,
  }
}

function cabinetDe(p: FaitPiece): Omit<NoeudFlux, 'colonne'> {
  const jobs = ['piece.transmettre']
  const t = p.transmission
  // Dépôt direct dans Tiime : rien ne part par mail ; l'agent du Mac dépose et relie la pièce
  // quand la règle de rapprochement la dit sûre. Le reste (décision, échec, sans objet) comme
  // pour l'envoi par mail.
  if (p.modeTransmission === 'tiime' && t === 'faite')
    return {
      id: 'cab:tiime',
      libelle: 'Déposée dans Tiime',
      genre: 'abouti',
      description:
        'Facture d’achat déposée dans Tiime par l’agent du Mac puis reliée à son opération (un dépôt pas encore relié est revu au passage suivant), ou reconnue déjà présente dans Tiime : jamais déposée deux fois.',
      jobs: ['portails.justificatifs'],
    }
  if (p.modeTransmission === 'tiime' && t === 'a_faire' && !p.echecCabinet)
    return {
      id: 'cab:tiime_attente',
      libelle: 'Attend son opération dans Tiime',
      genre: 'regle',
      description:
        'Dépôt direct : la facture attend son opération bancaire dans Tiime ; déposée et reliée seule quand le rapprochement est sûr, sinon proposée dans le bilan des justificatifs.',
      jobs: ['portails.justificatifs'],
    }
  if (t === 'faite')
    return {
      id: 'cab:envoyee',
      libelle: 'Envoyée au cabinet',
      genre: 'abouti',
      description: 'Facture d’achat envoyée à l’adresse de collecte du cabinet, une seule fois.',
      jobs,
    }
  if (t === 'a_decider' || t === 'incertain' || t === 'manuelle')
    return {
      id: 'cab:a_decider',
      libelle: 'Cabinet : à décider',
      genre: 'humain',
      description:
        'Facture reçue en perso (qui l’a réglée ?), envoi ou dépôt incertain, ou fichier refusé (trop gros, ou d’un type que le dépôt direct ne prend pas).',
      jobs,
      lien: '/pieces',
    }
  if (t === 'en_attente')
    return {
      id: 'cab:attente',
      libelle: 'Cabinet : attend un réglage',
      genre: 'humain',
      description: 'L’entité n’a pas d’adresse de collecte du cabinet.',
      jobs,
      lien: '/pieces',
    }
  if (t === 'a_faire' && p.echecCabinet)
    return {
      id: 'cab:echec',
      libelle: 'Cabinet en échec',
      genre: 'echec',
      description:
        'Trois essais d’envoi ou de dépôt sans succès : alerte pour les administrateurs.',
      jobs,
      lien: '/pieces',
    }
  if (t === 'a_faire')
    return {
      id: 'cab:en_cours',
      libelle: 'Envoi en cours',
      genre: 'regle',
      description: 'Envoi au prochain passage.',
      jobs,
    }
  return {
    id: 'cab:sans_objet',
    libelle: 'Pas pour le cabinet',
    genre: 'ecarte',
    description:
      'Vente, pièce qui n’est pas une facture d’achat, déjà chez le cabinet (plateforme) ou entité sans envoi.',
  }
}

/** Accumule nœuds et liens, dans l'ordre d'apparition (l'ordre de la première colonne compte). */
class Graphe {
  noeuds = new Map<string, NoeudFlux>()
  liens = new Map<string, LienFlux>()

  noeud(n: Omit<NoeudFlux, 'colonne'>, colonne: number) {
    if (!this.noeuds.has(n.id)) this.noeuds.set(n.id, { ...n, colonne })
    return n.id
  }

  lien(de: string, vers: string, valeur: number, unite: UniteFlux) {
    if (valeur <= 0) return
    const cle = `${de}>${vers}>${unite}`
    const l = this.liens.get(cle)
    if (l) l.valeur += valeur
    else this.liens.set(cle, { de, vers, valeur, unite })
  }

  /** Première colonne rangée par source (puis par libellé), le reste dans l'ordre d'apparition. */
  flux(colonnes: string[]): Flux {
    const rang = (n: NoeudFlux) => (n.colonne === 0 ? ORDRE_GROUPES.indexOf(n.groupe ?? '') : 99)
    const noeuds = [...this.noeuds.values()].sort(
      (a, b) =>
        rang(a) - rang(b) ||
        (a.colonne === 0 && b.colonne === 0 ? ordreCanal(a).localeCompare(ordreCanal(b)) : 0),
    )
    return { colonnes, noeuds, liens: [...this.liens.values()] }
  }
}

/**
 * Le diagramme des mails et des pièces : canal (chaque boîte, admin@, chaque portail…), tri du
 * mail (consigne de tri, Claude, écarté), nature (demande, pièces jointes, à vérifier), puis pour
 * chaque pièce la règle qui l'a rattachée, son classement, le Drive et le cabinet. Un mail compte
 * pour un jusqu'aux pièces jointes ; ensuite, chaque pièce compte pour une.
 */
export function construireFluxPieces(e: {
  mails: readonly FaitMail[]
  pieces: readonly FaitPiece[]
  /** Consignes « ignorer » actives, en clair, pour expliquer les mails écartés. */
  consignesIgnorer?: readonly string[]
}): Flux {
  const g = new Graphe()
  // Première colonne dans l'ordre des sources : boîtes (par adresse), admin@, puis les autres.
  const boites = [...new Set(e.mails.map((m) => m.boite).filter((b): b is string => !!b))].sort()
  for (const adresse of boites)
    g.noeud(
      {
        id: `canal:boite:${adresse}`,
        libelle: adresse,
        genre: 'entree',
        groupe: MAILS,
        description: 'Boîte personnelle relevée toutes les 15 minutes depuis le dernier point.',
        jobs: ['boites.relever'],
      },
      0,
    )
  if (e.mails.some((m) => m.boite === null && m.n > 0))
    g.noeud(
      {
        id: 'canal:admin',
        libelle: 'admin@',
        genre: 'entree',
        groupe: MAILS,
        description: 'Boîte admin@ : ses mails n’apportent que des pièces, sans tri.',
        jobs: ['gmail.synchroniser'],
      },
      0,
    )
  for (const p of e.pieces) {
    const c = canalDePiece(p)
    if (c) g.noeud(c, 0)
  }

  for (const m of e.mails) {
    if (m.boite === null) {
      g.noeud(PIECES_JOINTES, 2)
      g.lien('canal:admin', PIECES_JOINTES.id, m.n, 'mail')
      continue
    }
    const canal = `canal:boite:${m.boite}`
    if (m.classement === 'sans_rapport') {
      const id = g.noeud(
        {
          id: 'tri:ecartes',
          libelle: 'Écartés, oubliés',
          genre: 'ecarte',
          description:
            'Promotions, codes temporaires et consignes « ignorer » écartés sans modèle ; sinon Claude juge le mail sans rapport. Rien du mail n’est gardé.',
          ...(e.consignesIgnorer?.length && { details: [...e.consignesIgnorer] }),
          jobs: ['boites.relever'],
        },
        1,
      )
      g.lien(canal, id, m.n, 'mail')
      continue
    }
    const tri = m.consigne
      ? g.noeud(
          {
            id: `tri:consigne:${m.consigne}`,
            libelle: `Consigne : ${m.consigne}`,
            genre: 'regle',
            description:
              'Consigne de tri déclenchée par l’expéditeur ou l’objet (sans modèle), ou choisie par Claude d’après son « quand » : elle donne l’entité, l’exécutant, l’échéance.',
            jobs: ['boites.relever'],
          },
          1,
        )
      : g.noeud(
          {
            id: 'tri:claude',
            libelle: 'Trié par Claude',
            genre: 'claude',
            description:
              'Aucune consigne : Claude lit l’expéditeur, l’objet et l’extrait, puis décide de l’entité et de la nature.',
            jobs: ['boites.relever'],
          },
          1,
        )
    g.lien(canal, tri, m.n, 'mail')
    const nature =
      m.classement === 'demande_admin'
        ? g.noeud(
            {
              id: 'nature:demande',
              libelle: 'Demande : une tâche',
              genre: 'humain',
              description:
                'Tâche « Traiter la demande » avec sa date limite, confiée selon la consigne ; la réponse attendue d’un tiers y revient seule.',
              lien: '/taches',
            },
            2,
          )
        : m.classement === 'facture_ou_piece'
          ? g.noeud(PIECES_JOINTES, 2)
          : g.noeud(
              {
                id: 'nature:a_verifier',
                libelle: 'Mail à vérifier',
                genre: 'humain',
                description: 'Tri illisible ou douteux : « Vérifier un mail ».',
                lien: '/taches',
              },
              2,
            )
    g.lien(tri, nature, m.n, 'mail')
  }

  for (const p of e.pieces) {
    const canal = canalDePiece(p)
    let depart: string
    if (p.source === 'gmail') depart = g.noeud(PIECES_JOINTES, 2)
    else if (canal) depart = canal.id
    else continue
    const ratt = g.noeud(rattachementDe(p), 3)
    g.lien(depart, ratt, p.n, 'piece')
    if (p.statut !== 'rattachee') continue
    const classe = g.noeud(classementDe(p), 4)
    g.lien(ratt, classe, p.n, 'piece')
    const drive = g.noeud(driveDe(p), 5)
    g.lien(classe, drive, p.n, 'piece')
    const cabinet = g.noeud(cabinetDe(p), 6)
    g.lien(drive, cabinet, p.n, 'piece')
  }
  return g.flux(COLONNES_FLUX)
}

/** Le diagramme de la banque : compte, puis issue du rapprochement de chaque opération. */
export function construireFluxBanque(operations: readonly FaitOperation[]): Flux {
  const g = new Graphe()
  for (const compte of [...new Set(operations.map((o) => o.compte))].sort())
    g.noeud(
      {
        id: `compte:${compte}`,
        libelle: compte,
        genre: 'entree',
        groupe: 'Banque',
        description:
          'Opérations relevées par l’agrégateur bancaire deux fois par jour, ou importées d’un relevé CSV.',
        jobs: ['banque.relever'],
      },
      0,
    )
  const issues: Record<FaitOperation['etat'], Omit<NoeudFlux, 'colonne'>> = {
    auto: {
      id: 'rappr:auto',
      libelle: 'Rapprochée seule',
      genre: 'abouti',
      description:
        'Un paiement attendu correspond sûrement (montant, date, libellé, numéro) : rapproché, l’échéance se solde.',
      jobs: ['rapprocher', 'rapprocher.paiements'],
    },
    manuel: {
      id: 'rappr:manuel',
      libelle: 'Rapprochée à la main',
      genre: 'humain',
      description: 'Rapprochement décidé par un humain.',
    },
    a_trancher: {
      id: 'rappr:a_trancher',
      libelle: 'Rapprochement bancaire à trancher',
      genre: 'humain',
      description: 'Plusieurs candidats plausibles : décision « Rapprochement bancaire ».',
      lien: '/releves',
    },
    sans: {
      id: 'rappr:sans',
      libelle: 'Sans paiement attendu',
      genre: 'ecarte',
      description:
        'Aucun paiement attendu ne lui correspond (dépense courante, encaissement non suivi).',
    },
  }
  for (const o of operations)
    g.lien(`compte:${o.compte}`, g.noeud(issues[o.etat], 1), o.n, 'operation')
  return g.flux(['Compte', 'Rapprochement'])
}

/** Familles d'automatismes, dans l'ordre de la page. */
export const DOMAINES_AUTOMATISME = [
  'mails',
  'pieces',
  'portails',
  'banque',
  'echeances',
  'sante',
  'systeme',
] as const
export type DomaineAutomatisme = (typeof DOMAINES_AUTOMATISME)[number]

export const LIBELLES_DOMAINE_AUTOMATISME: Record<DomaineAutomatisme, string> = {
  mails: 'Mails',
  pieces: 'Pièces',
  portails: 'Portails',
  banque: 'Banque',
  echeances: 'Échéances et tâches',
  sante: 'Santé',
  systeme: 'Système',
}

/**
 * Ce que fait chaque job du worker, en clair. Un job absent de la liste s'affiche sous son nom ;
 * un test du worker vérifie que chaque job qu'il planifie y figure.
 */
export const AUTOMATISMES: Record<
  string,
  { libelle: string; role: string; domaine: DomaineAutomatisme; claude?: true }
> = {
  'gmail.synchroniser': {
    libelle: 'Relever admin@',
    role: 'Nouveaux mails depuis le dernier point, sur notification de Gmail ou toutes les 15 minutes.',
    domaine: 'mails',
  },
  'gmail.renouveler': {
    libelle: 'Renouveler la surveillance de Gmail',
    role: 'La notification de Gmail expire au bout de sept jours.',
    domaine: 'mails',
  },
  'boites.relever': {
    libelle: 'Relever et trier les boîtes personnelles',
    role: 'Tri de chaque mail (consignes, puis Claude), tâches et pièces créées ; une boîte d’espace privé ne range que dans l’espace, sauf réglage de sa propriétaire.',
    domaine: 'mails',
    claude: true,
  },
  'photos.assembler': {
    libelle: 'Assembler les photos',
    role: 'Un envoi complet devient une pièce PDF au nom de qui a envoyé.',
    domaine: 'pieces',
  },
  'piece.extraire': {
    libelle: 'Lire les pièces',
    role: 'Factur-X, sinon Claude ; texte intégral pour la recherche.',
    domaine: 'pieces',
    claude: true,
  },
  'piece.rattacher': {
    libelle: 'Rattacher les pièces',
    role: 'Entité, fournisseur, échéance ou paiement ; doublons ; « à vérifier » en cas de doute.',
    domaine: 'pieces',
  },
  'consignes_fournisseur.reexaminer': {
    libelle: 'Réexaminer avec les consignes de fournisseur',
    role: 'Une consigne nouvelle ou modifiée reprend les pièces à vérifier de son fournisseur.',
    domaine: 'pieces',
    claude: true,
  },
  'piece.placer': {
    libelle: 'Classer dans le plan',
    role: 'Claude choisit le dossier du plan de classement de l’entité.',
    domaine: 'pieces',
    claude: true,
  },
  'piece.ranger': {
    libelle: 'Ranger dans le Drive',
    role: 'Dépôt et renommage dans le dossier choisi ; nouvel essai en cas d’échec.',
    domaine: 'pieces',
  },
  'piece.transmettre': {
    libelle: 'Envoyer au cabinet',
    role: 'Factures d’achat vers l’adresse de collecte du cabinet, jamais deux fois ; jamais depuis un espace privé (à déposer soi-même). Balaie aussi les dépôts directs réservés depuis 15 minutes : jamais remis, annulés ; remis, incertains (« arrivé ? »).',
    domaine: 'pieces',
  },
  'pa.recuperer': {
    libelle: 'Récupérer la plateforme agréée',
    role: 'Factures déjà chez le cabinet, rangées sans renvoi.',
    domaine: 'pieces',
  },
  'portails.planifier': {
    libelle: 'Planifier les collectes',
    role: 'Seulement avec portails.planification (coupé depuis le 21/10/2026 : les collecteurs se lancent à la demande) : met en file les connexions dues, actives et non bloquées.',
    domaine: 'portails',
  },
  'portails.surveiller': {
    libelle: 'Surveiller le collecteur',
    role: 'Codes expirés, collectes muettes, collecteur absent.',
    domaine: 'portails',
  },
  'portails.disponibilite': {
    libelle: 'Demander la disponibilité',
    role: 'Une demande par titulaire, de 9 h à 20 h, avant toute reconnexion.',
    domaine: 'portails',
  },
  'portails.notifier': {
    libelle: 'Prévenir le titulaire d’un défi',
    role: 'Notification sur le téléphone dans la seconde (code, CAPTCHA).',
    domaine: 'portails',
  },
  'portails.recherche_demandee': {
    libelle: 'Chercher une opération dans les mails',
    role: 'Dans la seconde après « Chercher dans les mails » sur une opération à fournir ou à trancher du bilan : au nom du titulaire de la connexion, dans les boîtes qu’il voit et qui couvrent l’entité, sans le délai de sept jours ; dix minutes au moins entre deux recherches d’une même opération.',
    domaine: 'portails',
    claude: true,
  },
  'portails.justificatifs': {
    libelle: 'Rapprocher les justificatifs',
    role: 'Le passage du mois se lance à la demande, depuis Collecteurs (avec portails.planification seulement : du 2 au 10 du mois, dès 9 h, un passage par connexion de justificatifs sans passage complet ce mois-ci) ; à chaque tour, plan recalculé. Une fois le passage à blanc validé : tâches à trancher et à fournir, et une fois par jour, vingt opérations à fournir au plus cherchées dans les mails, au nom du titulaire qui l’a validé, dans les boîtes qu’il voit et qui couvrent l’entité (les siennes, celles des autres personnes de l’entité et, pour un administrateur, les boîtes personnelles sans entité), jamais celles d’un espace privé. Écriture ouverte par le titulaire : l’agent du Mac dépose puis relie les sûres, vingt au plus par passage, chacune réservée au registre avant l’envoi. Une pièce choisie par un humain dans le bilan (« C’est celle-ci ») vaut une sûre, avec les mêmes contrôles. Les recherches demandées depuis le bilan et pas encore menées sont reprises à chaque tour.',
    domaine: 'portails',
    claude: true,
  },
  'portails.apres_depot': {
    libelle: 'Suivre les dépôts à la main',
    role: 'Dès qu’une pièce envoyée à la main est rattachée, le plan des justificatifs est recalculé. Déposée par le titulaire de la connexion et sûre, écriture ouverte : son passage part, une seule fois par pièce, dans les deux heures.',
    domaine: 'portails',
  },
  'banque.relever': {
    libelle: 'Relever les banques',
    role: 'Opérations de l’agrégateur, puis rapprochement.',
    domaine: 'banque',
  },
  rapprocher: {
    libelle: 'Rapprocher',
    role: 'Reprend le rapprochement sur ce qui a changé depuis la veille.',
    domaine: 'banque',
  },
  'rapprocher.paiements': {
    libelle: 'Rapprocher les paiements changés',
    role: 'Chaque minute, relance le rapprochement des entités dont un paiement attendu vient d’être créé ou modifié (pièce lue après le débit, montant corrigé).',
    domaine: 'banque',
  },
  'echeances.generer': {
    libelle: 'Générer les échéances',
    role: 'Douze mois glissants d’après les règles ; une saisie manuelle n’est jamais réécrite.',
    domaine: 'echeances',
  },
  'taches.generer': {
    libelle: 'Créer les tâches à l’approche',
    role: '45 jours avant la date limite, affectées, recalées tant qu’ouvertes.',
    domaine: 'echeances',
  },
  'taches.notifier': {
    libelle: 'Prévenir des tâches confiées',
    role: 'Une notification groupée, 15 min après l’affectation, de 9 h à 20 h.',
    domaine: 'echeances',
  },
  'retards.calculer': {
    libelle: 'Calculer les retards',
    role: 'Paiements en retard et relevés manquants, avant le digest.',
    domaine: 'echeances',
  },
  'verifications.controler': {
    libelle: 'Contrôler les vérifications',
    role: 'Ce qui doit arriver chaque période (relevé, pièce) : constaté, ou une tâche s’il manque.',
    domaine: 'echeances',
  },
  'envois.suivre': {
    libelle: 'Suivre les courriels écrits dans Gmail',
    role: 'Reconnaît dans les envoyés de la boîte un courriel validé parti : l’envoi est fait, sa tâche reçoit l’issue choisie. Ne fait que lire.',
    domaine: 'mails',
  },
  'relances.preparer': {
    libelle: 'Préparer les relances',
    role: 'Une relance (jamais envoyée) par tiers attendu depuis plus de sept jours.',
    domaine: 'echeances',
  },
  'digest.slack': {
    libelle: 'Digest du matin',
    role: 'Message Slack par personne, jours ouvrés.',
    domaine: 'echeances',
  },
  'sante.agenda': {
    libelle: 'Repérer les soins dans les agendas',
    role: 'Titres et lieux des rendez-vous médicaux, sans modèle.',
    domaine: 'sante',
  },
  'sante.pieces': {
    libelle: 'Reporter les pièces de santé',
    role: 'Factures et décomptes reportés sur leurs soins.',
    domaine: 'sante',
  },
  'sante.rappeler': {
    libelle: 'Rappel des décomptes',
    role: 'Tâche mensuelle pour relever les décomptes santé.',
    domaine: 'sante',
  },
  'refacturations.dossiers': {
    libelle: 'Frais à refacturer des dossiers',
    role: 'Pièces d’un dossier à refacturer, factures avancées : une ligne chacune.',
    domaine: 'pieces',
  },
  'tva.recapitulatif': {
    libelle: 'Récapitulatif de TVA au cabinet',
    role: 'Le 10 du mois : les factures déposées comme preuve de débit le mois écoulé, et la TVA à ne pas déduire, en une tâche pour les responsables.',
    domaine: 'pieces',
  },
  'commandes.verifier': {
    libelle: 'Vérifier les factures des commandes',
    role: 'Commandes d’un portail d’achats : si leurs factures ne couvrent pas leur total, une tâche de suivi à une semaine, fermée quand elles arrivent.',
    domaine: 'pieces',
  },
  'photos.nettoyer': {
    libelle: 'Nettoyer le disque des envois',
    role: 'Fichiers des pièces rangées, écritures interrompues, orphelins.',
    domaine: 'systeme',
  },
  'ia.surveiller': {
    libelle: 'Surveiller la dépense IA',
    role: 'Alerte à la moitié du plafond mensuel, puis près du plafond ; chaque espace privé, sur son propre plafond, alerte sa titulaire.',
    domaine: 'systeme',
  },
  'sauvegarde.verifier': {
    libelle: 'Vérifier la sauvegarde',
    role: 'La sauvegarde de la nuit doit dater de moins de 36 heures.',
    domaine: 'systeme',
  },
  'sauvegarde.tester': {
    libelle: 'Rappel du test de restauration',
    role: 'Une tâche chaque mois.',
    domaine: 'systeme',
  },
}

// Cron à cinq champs (minute, heure, jour du mois, mois, jour de la semaine), sous-ensemble utilisé
// par le worker : « * », « */n », valeurs, listes et plages.

type Champ = { tout: boolean; valeurs: number[] }

function lireChamp(texte: string, min: number, max: number): Champ | null {
  if (texte === '*') return { tout: true, valeurs: intervalle(min, max, 1) }
  const pas = /^\*\/(\d+)$/.exec(texte)
  if (pas) {
    const n = Number(pas[1])
    return n > 0 ? { tout: false, valeurs: intervalle(min, max, n) } : null
  }
  const valeurs = new Set<number>()
  for (const morceau of texte.split(',')) {
    const plage = /^(\d+)(?:-(\d+))?$/.exec(morceau)
    if (!plage) return null
    const a = Number(plage[1])
    const b = plage[2] === undefined ? a : Number(plage[2])
    if (a < min || b > max || a > b) return null
    for (const v of intervalle(a, b, 1)) valeurs.add(v)
  }
  return { tout: false, valeurs: [...valeurs].sort((x, y) => x - y) }
}

function intervalle(a: number, b: number, pas: number): number[] {
  const r: number[] = []
  for (let v = a; v <= b; v += pas) r.push(v)
  return r
}

function lireCron(cron: string) {
  const champs = cron.trim().split(/\s+/)
  if (champs.length !== 5) return null
  const [minute, heure, jour, mois, semaine] = [
    lireChamp(champs[0]!, 0, 59),
    lireChamp(champs[1]!, 0, 23),
    lireChamp(champs[2]!, 1, 31),
    lireChamp(champs[3]!, 1, 12),
    lireChamp(champs[4]!, 0, 6),
  ]
  if (!minute || !heure || !jour || !mois || !semaine) return null
  return { minute, heure, jour, mois, semaine, brut: champs }
}

/**
 * Plus long écart possible entre deux passages prévus, en minutes : la tolérance avant de dire
 * qu'un automatisme est en retard. Null si le cron n'est pas lisible.
 */
export function ecartMaxCron(cron: string): number | null {
  const c = lireCron(cron)
  if (!c) return null
  // Jour du mois ou mois restreint : au plus un mois (31 jours) entre deux passages.
  if (!c.jour.tout || !c.mois.tout) return 31 * 1440
  const instants = c.heure.valeurs.flatMap((h) => c.minute.valeurs.map((m) => h * 60 + m))
  let ecartJour = 0
  for (let i = 1; i < instants.length; i++)
    ecartJour = Math.max(ecartJour, instants[i]! - instants[i - 1]!)
  const bouclage = instants[0]! + 1440 - instants[instants.length - 1]!
  // Jours de la semaine : le plus long saut entre deux jours permis (vendredi, lundi : 3).
  const jours = c.semaine.valeurs
  let sautJours = 1
  for (let i = 0; i < jours.length; i++) {
    const suivant = i + 1 < jours.length ? jours[i + 1]! : jours[0]! + 7
    sautJours = Math.max(sautJours, suivant - jours[i]!)
  }
  return Math.max(ecartJour, (sautJours - 1) * 1440 + bouclage)
}

const NOMS_JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi']

function heureLisible(h: number, m: number): string {
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, '0')}`
}

function liste(elements: string[]): string {
  return elements.length <= 1
    ? (elements[0] ?? '')
    : `${elements.slice(0, -1).join(', ')} et ${elements[elements.length - 1]}`
}

/** Cadence en français (« chaque minute », « chaque jour à 2 h 30 ») ; le cron brut sinon. */
export function cadenceLisible(cron: string | null | undefined): string {
  if (!cron) return 'à la demande'
  const c = lireCron(cron)
  if (!c) return cron
  const [m, h, j, mo, s] = c.brut as [string, string, string, string, string]
  if (mo !== '*') return cron
  if (h === '*' && j === '*' && s === '*') {
    if (m === '*') return 'chaque minute'
    const pas = /^\*\/(\d+)$/.exec(m)
    if (pas) return pas[1] === '15' ? 'tous les quarts d’heure' : `toutes les ${pas[1]} minutes`
    if (c.minute.valeurs.length === 1)
      return `chaque heure, à ${String(c.minute.valeurs[0]).padStart(2, '0')}`
    return cron
  }
  if (c.minute.tout || c.heure.tout || c.minute.valeurs.length !== 1) return cron
  const minute = c.minute.valeurs[0]!
  const heures = liste(c.heure.valeurs.map((x) => heureLisible(x, minute)))
  if (j === '*' && s === '*') return `chaque jour à ${heures}`
  if (j === '*') {
    if (s === '1-5') return `du lundi au vendredi à ${heures}`
    return `le ${liste(c.semaine.valeurs.map((x) => NOMS_JOURS[x]!))} à ${heures}`
  }
  if (s === '*' && c.jour.valeurs.length === 1) {
    const jour = c.jour.valeurs[0]!
    return `le ${jour === 1 ? '1er' : jour} de chaque mois à ${heures}`
  }
  return cron
}

/**
 * État d'un automatisme pour l'audit : en échec (dernier passage en erreur), en retard (rien de
 * réussi depuis plus que son écart prévu, avec une marge), jamais passé, à la demande (sans cron),
 * ou bon.
 */
export const ETATS_AUTOMATISME = ['ok', 'echec', 'retard', 'jamais', 'a_la_demande'] as const
export type EtatAutomatisme = (typeof ETATS_AUTOMATISME)[number]

/** Marge avant « en retard » : dix minutes, ou un dixième de l'écart prévu si c'est plus. */
export function margeRetardMinutes(ecart: number): number {
  return Math.max(10, Math.round(ecart / 10))
}

export function etatAutomatisme(
  a: {
    cron: string | null
    derniereReussite?: string | null | undefined
    echecsConsecutifs?: number | undefined
  },
  /** Démarrage du worker : un automatisme récent n'est pas « en retard » avant son premier tour. */
  demarreLe: string | null,
  maintenant: Date,
): EtatAutomatisme {
  if ((a.echecsConsecutifs ?? 0) > 0) return 'echec'
  if (!a.cron) return a.derniereReussite ? 'ok' : 'a_la_demande'
  const ecart = ecartMaxCron(a.cron)
  const reference = a.derniereReussite ?? demarreLe
  if (!reference || ecart === null) return a.derniereReussite ? 'ok' : 'jamais'
  const ecouleMinutes = (maintenant.getTime() - new Date(reference).getTime()) / 60_000
  if (ecouleMinutes > ecart + margeRetardMinutes(ecart)) return 'retard'
  return a.derniereReussite ? 'ok' : 'jamais'
}
